import { useCallback, useEffect, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { usePermissions } from "@/lib/permissions";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { Ban, FileText, Pause, Pencil, Play } from "lucide-react";
import { InfoHint } from "@/components/InfoHint";
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  Timeline,
  rowLink,
} from "@/components/PageUi";
import { ScheduleProgressMeter, relativeDays } from "@/components/SchedulesListPage";
import { cn } from "@/lib/utils";
import { formatCalendarDate, formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { formatDiscount } from "@/components/Discount";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { CADENCE_NOUN, ScheduleStatusPill, scheduleProgress } from "@/components/ScheduleShared";
import { PermissionGuard } from "@/components/PermissionGuard";
import { toast } from "sonner";

type ScheduleResponse = components["schemas"]["RecurringInvoiceScheduleResponse"];
type InvoiceResponse = components["schemas"]["InvoiceResponse"];

const INVOICE_PAGE_SIZE = 10;

function InvoiceStatusPill({ status }: { status: string | undefined }) {
  const tone =
    status === "Paid"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
      : status === "Issued"
        ? "border-accent-blue/30 bg-accent-blue/10 text-accent-blue"
        : status === "Void"
          ? "border-red-500/30 bg-red-500/10 text-red-700"
          : status === "PartiallyPaid"
            ? "border-amber-500/30 bg-amber-500/10 text-amber-700"
            : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex min-w-[64px] justify-center rounded-md border px-2 py-0.5 text-[11px] font-medium",
        tone,
      )}
    >
      {status === "PartiallyPaid" ? "Partial" : status ?? "-"}
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="flex items-center gap-1 text-muted-foreground">
        {label}
        {hint ? <InfoHint label={`More about ${label}`}>{hint}</InfoHint> : null}
      </dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

type RecurrenceCadence = components["schemas"]["RecurrenceCadence"];

const CADENCE_MONTHS: Record<Exclude<RecurrenceCadence, "Weekly">, number> = {
  Monthly: 1,
  Quarterly: 3,
  Annually: 12,
};

/** A date `n` periods after `anchor`, clamped to the end of shorter months (31 Jan + 1 month = 28 Feb). */
export function addPeriods(anchor: Date, cadence: RecurrenceCadence, n: number): Date {
  if (cadence === "Weekly") {
    return new Date(anchor.getTime() + n * 7 * 86_400_000);
  }
  const months = CADENCE_MONTHS[cadence] * n;
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(anchor.getUTCDate(), lastDay),
      anchor.getUTCHours(),
      anchor.getUTCMinutes(),
    ),
  );
}

const sameDay = (a: Date, b: Date) => a.toISOString().slice(0, 10) === b.toISOString().slice(0, 10);

const UPCOMING_SHOWN = 4;

/** Anchored on the start date so month-end schedules keep their day; if that misses the server's next run, the server wins. */
function upcomingRuns(schedule: ScheduleResponse): { date: Date; occurrence: number }[] {
  if (schedule.status !== "Active" || !schedule.nextRunAt || !schedule.cadence) return [];
  const cadence = schedule.cadence;
  const next = new Date(schedule.nextRunAt);
  const generated = Number(schedule.occurrencesGenerated ?? 0);
  const total = schedule.totalOccurrences != null ? Number(schedule.totalOccurrences) : null;
  const remaining = total != null ? Math.max(0, total - generated) : Infinity;
  const count = Math.min(UPCOMING_SHOWN, remaining);

  const start = schedule.startDate ? new Date(schedule.startDate) : null;
  const anchoredOnStart = start && sameDay(addPeriods(start, cadence, generated), next);

  return Array.from({ length: count }, (_, k) => ({
    date: anchoredOnStart ? addPeriods(start, cadence, generated + k) : addPeriods(next, cadence, k),
    occurrence: generated + k + 1,
  }));
}

export interface SchedulePageProps {
  scheduleId: string;
  onBack: () => void;
  onEditSchedule: (id: string) => void;
  onInvoiceClick: (id: string) => void;
  onCustomerClick: (id: string) => void;
}

export function SchedulePage({
  scheduleId,
  onBack,
  onEditSchedule,
  onInvoiceClick,
  onCustomerClick,
}: SchedulePageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageInvoices");

  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [invoicesCursorStack, setInvoicesCursorStack] = useState<string[]>([]);
  const [invoicesNextCursor, setInvoicesNextCursor] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    const { data, error: apiError } = await api.GET("/v1/recurring-invoices/{id}", {
      params: { path: { id: scheduleId } },
    });
    if (apiError || !data) {
      setError("Failed to load recurring invoice");
      return;
    }
    setSchedule(data);
  }, [api, scheduleId]);

  const fetchInvoices = useCallback(
    async (cursor?: string) => {
      setInvoicesLoading(true);
      try {
        const query: Record<string, string | number> = {
          Limit: INVOICE_PAGE_SIZE,
          RecurringScheduleId: scheduleId,
        };
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/invoices", {
          params: { query },
        });
        if (apiError || !data) {
          console.error("Failed to load schedule invoices:", apiError);
          return;
        }
        setInvoices(data.data ?? []);
        const next = data.meta?.cursors?.next ?? null;
        setInvoicesNextCursor(next);
        setInvoicesHasMore(next !== null);
      } finally {
        setInvoicesLoading(false);
      }
    },
    [api, scheduleId],
  );

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      await Promise.all([fetchSchedule(), fetchInvoices()]);
      if (!cancelled) setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchSchedule, fetchInvoices, reloadKey]);

  async function pauseOrResume(action: "pause" | "resume") {
    setActing(true);
    const { data, error: apiError } =
      action === "pause"
        ? await api.POST("/v1/recurring-invoices/{id}/pause", {
            params: { path: { id: scheduleId } },
          })
        : await api.POST("/v1/recurring-invoices/{id}/resume", {
            params: { path: { id: scheduleId } },
          });
    setActing(false);

    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, `Failed to ${action} recurring invoice`));
      return;
    }
    setSchedule(data);
    toast.success(
      action === "pause" ? "Recurring invoice paused" : "Recurring invoice resumed",
    );
  }

  async function confirmCancel() {
    setCancelOpen(false);
    setActing(true);
    const { data, error: apiError } = await api.POST("/v1/recurring-invoices/{id}/cancel", {
      params: { path: { id: scheduleId } },
    });
    setActing(false);

    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to cancel recurring invoice"));
      return;
    }
    setSchedule(data);
    toast.success("Recurring invoice cancelled");
  }

  function handleInvoicesNext() {
    if (!invoicesNextCursor) return;
    setInvoicesCursorStack((prev) => [...prev, invoicesNextCursor]);
    fetchInvoices(invoicesNextCursor);
  }

  function handleInvoicesPrev() {
    const newStack = [...invoicesCursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setInvoicesCursorStack(newStack);
    fetchInvoices(prevCursor);
  }

  if (loading) {
    return (
      <PageContainer>
        <BackLink onClick={onBack}>Back to recurring invoices</BackLink>
        <LoadingState label="Loading recurring invoice..." />
      </PageContainer>
    );
  }

  if (error || !schedule) {
    return (
      <PageContainer>
        <BackLink onClick={onBack}>Back to recurring invoices</BackLink>
        <ErrorState
          message={error ?? "That recurring invoice could not be found."}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </PageContainer>
    );
  }

  const isEditable =
    schedule.status === "Active" || schedule.status === "Paused";
  const invoicesPage = invoicesCursorStack.length + 1;
  const customerName = schedule.customer?.name ?? "Unknown customer";
  const upcoming = upcomingRuns(schedule);

  return (
    <PageContainer>
      <BackLink onClick={onBack}>Back to recurring invoices</BackLink>
      <PageHeader
        eyebrow="Recurring invoice"
        title={customerName}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-foreground">{schedule.cadence}</span>
            <span aria-hidden>·</span>
            <span className="tabular-nums">{scheduleProgress(schedule)}</span>
            <span aria-hidden>·</span>
            <span>{schedule.currency}</span>
            {schedule.status === "Active" && schedule.nextRunAt ? (
              <>
                <span aria-hidden>·</span>
                <span>Next {formatDate(schedule.nextRunAt)}</span>
              </>
            ) : null}
          </span>
        }
        actions={
          <>
            <ScheduleStatusPill status={schedule.status ?? undefined} />
            {isEditable && (
              <PermissionGuard
                allowed={canManage}
                reason="Only owners and admins can manage recurring invoices"
              >
                <Button
                  variant="outline"
                  onClick={() => onEditSchedule(scheduleId)}
                  className="gap-1.5"
                >
                  <Pencil className="size-4" />
                  Edit
                </Button>
              </PermissionGuard>
            )}
            {schedule.status === "Active" && (
              <PermissionGuard
                allowed={canManage}
                reason="Only owners and admins can manage recurring invoices"
              >
                <Button
                  variant="outline"
                  disabled={acting}
                  onClick={() => pauseOrResume("pause")}
                  className="gap-1.5"
                >
                  {acting ? <Spinner className="size-4" /> : <Pause className="size-4" />}
                  Pause
                </Button>
              </PermissionGuard>
            )}
            {schedule.status === "Paused" && (
              <PermissionGuard
                allowed={canManage}
                reason="Only owners and admins can manage recurring invoices"
              >
                <Button
                  disabled={acting}
                  onClick={() => pauseOrResume("resume")}
                  className="gap-1.5"
                >
                  {acting ? <Spinner className="size-4" /> : <Play className="size-4" />}
                  Resume
                </Button>
              </PermissionGuard>
            )}
            {isEditable && (
              <PermissionGuard
                allowed={canManage}
                reason="Only owners and admins can manage recurring invoices"
              >
                <Button
                  variant="outline"
                  disabled={acting}
                  onClick={() => setCancelOpen(true)}
                  className="gap-1.5 text-destructive hover:text-destructive"
                >
                  <Ban className="size-4" />
                  Cancel
                </Button>
              </PermissionGuard>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {/* Why nothing is coming: the first question on a paused or ended schedule. */}
        {schedule.status === "Paused" ? (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="text-sm">
              <span className="font-medium">Paused.</span>{" "}
              <span className="text-muted-foreground">
                No invoices are generated until it is resumed.
              </span>
            </CardContent>
          </Card>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <Field label="Customer">
                  <button
                    type="button"
                    className="font-medium hover:underline"
                    onClick={() => schedule.customerId && onCustomerClick(schedule.customerId)}
                  >
                    {customerName}
                  </button>
                </Field>
                <Field label="Cadence">
                  {schedule.cadence}
                  <span className="ml-1 text-muted-foreground">
                    (every {CADENCE_NOUN[schedule.cadence ?? "Monthly"]})
                  </span>
                </Field>
                <Field label="Started">{formatDate(schedule.startDate)}</Field>
                <Field label="Progress">
                  <div className="max-w-40">
                    <ScheduleProgressMeter schedule={schedule} />
                  </div>
                </Field>
                <Field label="Sending">
                  {schedule.autoSend ? "Sent automatically" : "Drafts for review"}
                </Field>
                <Field
                  label="Payment terms"
                  hint="Sets the due date on every generated invoice: this many days after each invoice's issue date."
                >
                  {schedule.paymentTermsDays != null
                    ? Number(schedule.paymentTermsDays) === 0
                      ? "Due on receipt"
                      : `${Number(schedule.paymentTermsDays)} days`
                    : "Organisation default"}
                </Field>
                <Field
                  label="Invoice discount"
                  hint="Applied to every generated invoice, after any per-line discounts, and spread across the lines before tax."
                >
                  {formatDiscount(schedule.discountType, schedule.discountValue, schedule.currency)}
                </Field>
                {schedule.lastGeneratedAt ? (
                  <Field label="Last generated">{formatDateTime(schedule.lastGeneratedAt)}</Field>
                ) : null}
                {schedule.memo ? <Field label="Memo">{schedule.memo}</Field> : null}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1 text-base">
                Coming up
                <InfoHint label="More about upcoming invoices">
                  Projected from the cadence. Each invoice is raised on the day shown and bills the
                  line template below. Lines without a fixed price bill the product's price on that day.
                </InfoHint>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming.length > 0 ? (
                <Timeline
                  entries={upcoming.map((run, i) => ({
                    id: run.date.toISOString(),
                    title: formatCalendarDate(run.date),
                    detail: (
                      <>
                        {schedule.totalOccurrences != null
                          ? `Invoice ${run.occurrence} of ${Number(schedule.totalOccurrences)}`
                          : `Invoice ${run.occurrence}`}
                        {i === 0 ? ` · ${relativeDays(run.date.toISOString())?.toLowerCase()}` : ""}
                      </>
                    ),
                  }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {schedule.status === "Paused"
                    ? "Nothing is scheduled while this is paused."
                    : schedule.status === "Completed"
                      ? "Every invoice in this run has been generated."
                      : schedule.status === "Cancelled"
                        ? "This recurring invoice was cancelled and raises nothing further."
                        : "Nothing scheduled."}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Line template</CardTitle>
          </CardHeader>
          <CardContent>
            <TableShell className="rounded-lg bg-transparent backdrop-blur-none">
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Product</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Quantity</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Unit price</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Discount</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {(schedule.lineItems ?? []).map((li) => (
                    <TableRow key={li.id}>
                      <TableCell className="font-medium">{li.productName ?? "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{li.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {li.unitPriceOverride != null ? (
                          <span>
                            {formatCurrency(li.unitPriceOverride, schedule.currency)}
                            <span className="ml-1.5 text-xs text-muted-foreground">fixed</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Tracks product price</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatDiscount(li.discountType, li.discountValue, schedule.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generated invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <LoadingState label="Loading invoices..." />
            ) : invoices.length === 0 ? (
              <EmptyState icon={FileText} title="No invoices generated yet">
                {schedule.status === "Active" && schedule.nextRunAt
                  ? `The first arrives ${formatDate(schedule.nextRunAt)}.`
                  : "Invoices appear here as the schedule raises them."}
              </EmptyState>
            ) : (
              <>
                <TableShell className="rounded-lg bg-transparent backdrop-blur-none">
                  <Table>
                    <TableHeader>
                      <HeaderRow>
                        <TableHead className={TH}>Invoice</TableHead>
                        <TableHead className={TH}>Issued</TableHead>
                        <TableHead className={TH}>Due</TableHead>
                        <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                        <TableHead className={cn(TH, "w-[100px] text-right")}>Status</TableHead>
                      </HeaderRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          {...rowLink(() => invoice.id && onInvoiceClick(invoice.id))}
                        >
                          <TableCell className="font-mono text-[13px] font-semibold">
                            {invoice.documentNumber || (
                              <span className="font-sans font-normal text-muted-foreground">Draft</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {invoice.status === "Draft"
                              ? `Created ${formatDate(invoice.createdAt)}`
                              : formatDate(invoice.issueDate)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {invoice.dueDate ? formatDate(invoice.dueDate) : "-"}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {formatCurrency(invoice.totalAmount, invoice.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <InvoiceStatusPill status={invoice.status ?? undefined} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableShell>

                <Pager
                  page={invoicesPage}
                  hasPrevious={invoicesPage > 1}
                  hasNext={invoicesHasMore}
                  onPrevious={handleInvoicesPrev}
                  onNext={handleInvoicesNext}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel recurring invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This recurring invoice will stop generating invoices permanently - a cancelled
              recurring invoice cannot be resumed. Invoices it has already generated are
              unaffected. If the stop might be temporary, pause it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep recurring invoice</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmCancel}>
              Cancel recurring invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
